import { useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  FaCloudUploadAlt,
  FaFileExcel,
  FaDownload,
  FaPaperPlane,
  FaEraser,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaFileArchive,
  FaTable,
  FaInfoCircle,
} from 'react-icons/fa';
import Rut from 'rutjs';
import * as XLSX from 'xlsx';
import api from '../../api/apiweb-massiveFacturaUploader.js';
import { erroresDeRespuesta, mensajeDeRespuesta } from '../../utils/erroresApi';
import useSucursales from '../../hooks/useSucursales';
import '../emisorMasivo/EmisorMasivo.scss';

// Tipo de documento de referencia para Orden de Compra y HES (ver constants/tiposReferencia.js)
const TIPO_REF_ORDEN_COMPRA = '801';
// HES se envía como código de texto libre "HES" (no el numérico 816).
const TIPO_REF_HES = 'HES';

// Columnas del Excel. El orden define la plantilla y la vista previa.
// Las columnas de montos (iva / valor total) dependen de si es afecta o exenta.
// La descripción va junto al nombre de producto. Su valor puede quedar en blanco,
// pero la columna siempre forma parte del layout para facilitar el llenado.
// Los datos del receptor llevan el sufijo "receptor" para no confundirlos con los
// del emisor, que ahora también viajan en la planilla.

// Dirección desde la que se emite el documento. Va como texto libre y no se cruza
// con la tabla de sucursales: el cliente puede escribir un nombre que no calce con
// ninguna sucursal cargada, así que se manda tal cual lo escribió. Si la fila las
// deja en blanco, el backend emite con la dirección de la empresa, como hasta ahora.
const EMISOR_COLUMNS = [
  'direccion emisor',
  'comuna emisor',
  'ciudad emisor',
  'codigo sii emisor',
];

const RECEPTOR_PRODUCT_COLUMNS = [
  'rut receptor',
  'razon social receptor',
  'giro receptor',
  'direccion receptor',
  'comuna receptor',
  'ciudad receptor',
  'email receptor',
  'fecha emision',
  'nombre producto',
  'descripcion',
  'cantidad',
  'unidad',
  'precio',
];

const REFERENCE_COLUMNS = [
  'folio orden compra',
  'fecha orden compra',
  'razon orden compra',
  'folio hes',
  'fecha hes',
  'razon hes',
];

// Columnas cuyo valor es opcional (la columna debe existir igual).
const VALUE_OPTIONAL_COLUMNS = new Set([
  'email receptor',
  'descripcion',
  'razon orden compra',
  'razon hes',
  'tipo venta',
  ...EMISOR_COLUMNS,
]);

const COLUMN_LABELS = {
  'direccion emisor': 'Dirección Emisor',
  'comuna emisor': 'Comuna Emisor',
  'ciudad emisor': 'Ciudad Emisor',
  'codigo sii emisor': 'Código SII Emisor',
  'rut receptor': 'RUT Receptor',
  'razon social receptor': 'Razón Social Receptor',
  'giro receptor': 'Giro Receptor',
  'direccion receptor': 'Dirección Receptor',
  'comuna receptor': 'Comuna Receptor',
  'ciudad receptor': 'Ciudad Receptor',
  'email receptor': 'Email Receptor',
  'fecha emision': 'Fecha Emisión',
  'nombre producto': 'Nombre Producto',
  'descripcion': 'Descripción',
  'cantidad': 'Cantidad',
  'unidad': 'Unidad',
  'precio': 'Precio',
  'iva': 'IVA',
  'valor total': 'Valor Total',
  'forma pago': 'Forma de Pago',
  'tipo venta': 'Tipo de Venta',
  'folio orden compra': 'Folio Orden Compra',
  'fecha orden compra': 'Fecha Orden Compra',
  'razon orden compra': 'Razón Orden Compra',
  'folio hes': 'Folio HES',
  'fecha hes': 'Fecha HES',
  'razon hes': 'Razón HES',
};

// Nombres equivalentes que se aceptan para una misma columna.
const COLUMN_ALIASES = {
  'email receptor': ['email receptor', 'email', 'correo'],
};

const aliasesDe = (col) => COLUMN_ALIASES[col] || [col];

const NUM_COLUMNS = new Set(['cantidad', 'precio', 'iva', 'valor total']);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeKey = (key) =>
  key
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s_]+/g, ' ');

const getColumnValue = (row, aliases) => {
  for (const alias of aliases) {
    if (row[alias] !== undefined && row[alias] !== '') return row[alias];
  }
  return '';
};

const sanitizeText = (value, maxLength) => {
  let text = String(value ?? '').trim();
  text = text.normalize('NFD').replace(/[̀-ͯ]/g, '');
  text = text.replace(/ñ/gi, 'N');
  text = text.replace(/[^A-Za-z0-9\s.,-]/g, '');
  if (maxLength) text = text.slice(0, maxLength);
  return text;
};

const formatAndValidateDate = (value) => {
  if (!value) return { formatted: '', validFormat: false, isNotFuture: false };
  let formatted = '';
  if (value instanceof Date) {
    formatted = value.toISOString().split('T')[0];
  } else if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    if (!date) return { formatted: '', validFormat: false, isNotFuture: false };
    formatted = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
  } else {
    const cleaned = String(value).trim().replace(/[–—]/g, '-').replace(/\s+/g, '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return { formatted: cleaned, validFormat: false, isNotFuture: false };
    }
    formatted = cleaned;
  }
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;
  return { formatted, validFormat: true, isNotFuture: formatted <= todayStr };
};

// Solo se aceptan contado o crédito.
const FORMA_PAGO_MAP = {
  'contado': '1',
  'credito': '2',
  'crédito': '2',
};

const FORMA_PAGO_LABEL = { '1': 'Contado', '2': 'Crédito' };

// TpoTranVenta del DTE. Va por código y no por texto: escribir "1" no admite
// variantes, "activo fijo" sí. La columna puede ir vacía y en ese caso es venta del
// giro, que es lo que se emite hoy.
const TIPO_VENTA_LABEL = { '1': 'Ventas del Giro', '2': 'Venta Activo Fijo', '3': 'Venta Bien Raíz' };

const TIPO_VENTA_DEFECTO = '1';

// Solo viajan los campos de emisor que la fila trae escritos: los que falten los
// completa el backend con los datos de la empresa. Si no hay ninguno, la fila sale
// sin el bloque, exactamente como antes de que existieran estas columnas.
const bloqueEmisor = (row) => {
  const emisor = {};
  if (row['direccion emisor']) emisor.direccion = row['direccion emisor'];
  if (row['comuna emisor']) emisor.comuna = row['comuna emisor'];
  if (row['ciudad emisor']) emisor.ciudad = row['ciudad emisor'];
  if (row['codigo sii emisor']) emisor.codigo_sii = row['codigo sii emisor'];
  return Object.keys(emisor).length > 0 ? { emisor } : {};
};

const processRut = (rut) => {
  try {
    const parsed = new Rut(rut);
    if (!parsed.validate()) return { valid: false };
    return { valid: true, rutWithoutDv: parsed.rut, rutFormatted: parsed.getNiceRut() };
  } catch {
    return { valid: false };
  }
};

/**
 * Emisor masivo de facturas. Reutilizable para afecta (33) y exenta (34) según la prop tipoDocumento.
 * El backend recibe filas planas y arma los DTE + asigna folios (endpoint massivefacturauploader).
 */
const EmisorMasivoFactura = ({ empresas, tipoDocumento = '33' }) => {
  const esExenta = tipoDocumento === '34';
  const tipoLabel = esExenta ? 'Exenta' : 'Afecta';

  // La plantilla sale con la casa matriz ya escrita en las columnas de emisor, que
  // es desde donde se emite casi siempre; el cliente la reemplaza en las filas que
  // salgan desde otra dirección.
  const { sucursal: sucursalPorDefecto } = useSucursales(empresas?.[0]?.rut);

  // En afecta el usuario ingresa el IVA; en exenta no aplica IVA.
  const requiredColumns = useMemo(
    () =>
      esExenta
        ? [...EMISOR_COLUMNS, ...RECEPTOR_PRODUCT_COLUMNS, 'valor total', 'forma pago', 'tipo venta', ...REFERENCE_COLUMNS]
        : [...EMISOR_COLUMNS, ...RECEPTOR_PRODUCT_COLUMNS, 'iva', 'valor total', 'forma pago', 'tipo venta', ...REFERENCE_COLUMNS],
    [esExenta],
  );

  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessageModal, setErrorMessageModal] = useState('');
  const [processedFolios, setProcessedFolios] = useState([]);
  const [processedDocs, setProcessedDocs] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
      const normalizedData = rawData.map((row) => {
        const normalizedRow = {};
        Object.keys(row).forEach((key) => {
          normalizedRow[normalizeKey(key)] = row[key];
        });
        return normalizedRow;
      });
      validateAndSetData(normalizedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (event) => processFile(event.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      processFile(file);
    } else {
      setErrors(['Solo se aceptan archivos .xlsx o .xls']);
    }
  };

  const validateAndSetData = (data) => {
    const headers = Object.keys(data[0] || {});
    const missingCols = requiredColumns.filter(
      (col) => !VALUE_OPTIONAL_COLUMNS.has(col) && !aliasesDe(col).some((a) => headers.includes(a)),
    );
    if (missingCols.length > 0) {
      setErrors([`Faltan las siguientes columnas: ${missingCols.join(', ')}`]);
      setRows([]);
      return;
    }

    const validatedRows = data.map((row) => {
      const rowErrors = [];

      // Emisor: dirección desde la que se emite. El respaldo del backend es por
      // campo, no por bloque: una celda vacía toma ese dato de la empresa, así que
      // una fila puede traer solo la dirección y heredar comuna y ciudad.
      // La dirección va a DirOrigen, que el SII limita a 60 (los 70 son de DirRecep).
      row['direccion emisor'] = sanitizeText(row['direccion emisor'], 60);
      row['comuna emisor'] = sanitizeText(row['comuna emisor'], 20).toUpperCase();
      row['ciudad emisor'] = sanitizeText(row['ciudad emisor'], 20);

      // Código que el SII le asigna al local. Opcional: sin él, el DTE sale sin el
      // nodo CdgSIISucur. No se trunca como los textos, porque un código a medias
      // apunta a otro local: si no calza, la fila se corrige.
      const codigoSii = String(row['codigo sii emisor'] ?? '').trim();
      if (codigoSii && !/^\d{1,9}$/.test(codigoSii)) {
        rowErrors.push('Código SII del emisor inválido (hasta 9 dígitos)');
      }
      row['codigo sii emisor'] = codigoSii;

      // Receptor: RUT
      const { valid, rutWithoutDv, rutFormatted } = processRut(row['rut receptor']);
      if (!valid) {
        rowErrors.push('RUT del receptor inválido');
      } else {
        row['rut_display'] = rutFormatted;
        row['rut receptor'] = rutWithoutDv;
      }

      // Receptor: razón social, giro, dirección, comuna, ciudad
      const razon = sanitizeText(row['razon social receptor'], 100);
      if (!razon) rowErrors.push('Razón social del receptor vacía');
      row['razon social receptor'] = razon;

      const giro = sanitizeText(row['giro receptor'], 80);
      if (!giro) rowErrors.push('Giro del receptor vacío');
      row['giro receptor'] = giro;

      const direccion = sanitizeText(row['direccion receptor'], 70);
      if (!direccion) rowErrors.push('Dirección del receptor vacía');
      row['direccion receptor'] = direccion;

      const comuna = sanitizeText(row['comuna receptor'], 20);
      if (!comuna) rowErrors.push('Comuna del receptor vacía');
      row['comuna receptor'] = comuna.toUpperCase();

      const ciudad = sanitizeText(row['ciudad receptor'], 20);
      if (!ciudad) rowErrors.push('Ciudad del receptor vacía');
      row['ciudad receptor'] = ciudad;

      // Email opcional: puede venir vacío. Si trae valor, se valida formato y longitud.
      const email = String(getColumnValue(row, aliasesDe('email receptor')) || '').trim();
      if (email) {
        if (!EMAIL_REGEX.test(email)) rowErrors.push('Email del receptor inválido');
        else if (email.length > 80) rowErrors.push('Email del receptor excede 80 caracteres');
      }
      row['email receptor'] = email;

      // Producto
      const nombreProducto = sanitizeText(row['nombre producto'], 80);
      if (!nombreProducto) rowErrors.push('Nombre de producto vacío');
      row['nombre producto'] = nombreProducto;

      row['descripcion'] = sanitizeText(getColumnValue(row, ['descripcion']), 1000);
      row['unidad'] = String(row['unidad'] || '').trim().slice(0, 4);

      const cantidad = parseFloat(row['cantidad']);
      if (isNaN(cantidad) || cantidad <= 0) rowErrors.push('Cantidad inválida');
      row['cantidad'] = isNaN(cantidad) ? '' : cantidad;

      const precio = parseFloat(row['precio']);
      if (isNaN(precio) || precio <= 0) rowErrors.push('Precio inválido');
      row['precio'] = isNaN(precio) ? '' : precio;

      // Neto = cantidad * precio
      const neto = !isNaN(cantidad) && !isNaN(precio) ? Math.round(cantidad * precio) : 0;

      // IVA: lo ingresa el usuario (solo afecta). En exenta no aplica (queda en 0).
      let iva = NaN;
      if (!esExenta) {
        iva = parseFloat(row['iva']);
        if (isNaN(iva) || iva < 0) rowErrors.push('IVA inválido');
        row['iva'] = isNaN(iva) ? '' : iva;
      }
      const ivaFinal = esExenta ? 0 : iva; // NaN si es afecta sin IVA válido

      // Valor total ingresado por el usuario: debe cuadrar con neto + IVA.
      const valorTotal = parseFloat(row['valor total']);
      const esperado = neto + (isNaN(ivaFinal) ? 0 : ivaFinal);
      if (isNaN(valorTotal) || valorTotal < 0) {
        rowErrors.push('Valor total inválido');
      } else if (!isNaN(ivaFinal) && valorTotal !== esperado) {
        rowErrors.push(esExenta ? 'Valor total no cuadra (= neto)' : 'Valor total no cuadra (neto + IVA)');
      }
      row['valor total'] = isNaN(valorTotal) ? '' : valorTotal;

      row['_neto'] = neto;
      row['_iva'] = isNaN(ivaFinal) ? 0 : ivaFinal;
      row['_total'] = isNaN(valorTotal) ? esperado : valorTotal;

      // Forma de pago: obligatoria, solo contado o crédito.
      const rawPago = String(getColumnValue(row, ['forma pago', 'tipo pago']) || '').trim().toLowerCase();
      const codigoPago = FORMA_PAGO_MAP[rawPago];
      if (!rawPago) rowErrors.push('Forma de pago faltante');
      else if (!codigoPago) rowErrors.push('Forma de pago inválida (contado o crédito)');
      row['_forma_pago'] = codigoPago || '';
      row['forma pago'] = codigoPago ? FORMA_PAGO_LABEL[codigoPago] : row['forma pago'];

      // Tipo de venta (TpoTranVenta): opcional. Vacío es venta del giro, que es lo
      // que se emitía antes de que existiera la columna.
      const rawVenta = String(getColumnValue(row, ['tipo venta']) ?? '').trim();
      let codigoVenta = TIPO_VENTA_DEFECTO;
      if (rawVenta) codigoVenta = TIPO_VENTA_LABEL[rawVenta] ? rawVenta : '';
      if (rawVenta && !codigoVenta) {
        rowErrors.push('Tipo de venta inválido (1, 2 o 3)');
      }
      row['_tipo_venta'] = codigoVenta || '';
      row['tipo venta'] = codigoVenta ? `${codigoVenta} · ${TIPO_VENTA_LABEL[codigoVenta]}` : row['tipo venta'];

      // Fecha emisión (no futura)
      const fechaEmisionRes = formatAndValidateDate(getColumnValue(row, ['fecha emision']));
      row['fecha emision'] = fechaEmisionRes.formatted;
      if (!fechaEmisionRes.formatted) rowErrors.push('Fecha de emisión faltante');
      else if (!fechaEmisionRes.validFormat) rowErrors.push('Fecha emisión inválida (yyyy-mm-dd)');
      else if (!fechaEmisionRes.isNotFuture) rowErrors.push('Fecha emisión mayor a hoy');

      // Referencia 1: Orden de Compra
      const folioOC = String(row['folio orden compra'] || '').trim();
      if (!folioOC) rowErrors.push('Folio Orden de Compra faltante');
      row['folio orden compra'] = folioOC;
      const fechaOCRes = formatAndValidateDate(getColumnValue(row, ['fecha orden compra']));
      row['fecha orden compra'] = fechaOCRes.formatted;
      if (!fechaOCRes.formatted) rowErrors.push('Fecha Orden de Compra faltante');
      else if (!fechaOCRes.validFormat) rowErrors.push('Fecha OC inválida (yyyy-mm-dd)');
      else if (!fechaOCRes.isNotFuture) rowErrors.push('Fecha OC mayor a hoy');
      row['razon orden compra'] = sanitizeText(getColumnValue(row, ['razon orden compra']), 90);

      // Referencia 2: HES
      const folioHES = String(row['folio hes'] || '').trim();
      if (!folioHES) rowErrors.push('Folio HES faltante');
      row['folio hes'] = folioHES;
      const fechaHESRes = formatAndValidateDate(getColumnValue(row, ['fecha hes']));
      row['fecha hes'] = fechaHESRes.formatted;
      if (!fechaHESRes.formatted) rowErrors.push('Fecha HES faltante');
      else if (!fechaHESRes.validFormat) rowErrors.push('Fecha HES inválida (yyyy-mm-dd)');
      else if (!fechaHESRes.isNotFuture) rowErrors.push('Fecha HES mayor a hoy');
      row['razon hes'] = sanitizeText(getColumnValue(row, ['razon hes']), 90);

      return { ...row, error: rowErrors.join('; ') };
    });

    setErrors([]);
    setRows(validatedRows);
  };

  const buildPayloadRow = (row) => ({
    tipo_documento: tipoDocumento,
    fecha_emision: row['fecha emision'],
    forma_pago: row['_forma_pago'],
    tipo_venta: row['_tipo_venta'],
    ...bloqueEmisor(row),
    receptor: {
      rut: row['rut receptor'],
      razon_social: row['razon social receptor'],
      giro: row['giro receptor'],
      direccion: row['direccion receptor'],
      comuna: row['comuna receptor'],
      ciudad: row['ciudad receptor'],
      tipo_compra: '1',
      email: row['email receptor'],
    },
    producto: {
      nombre: row['nombre producto'],
      descripcion: row.descripcion || '',
      cantidad: row.cantidad,
      unidad: row.unidad || '',
      precio: row.precio,
    },
    totales: esExenta
      ? { exento: row._total, total: row._total }
      : { neto: row._neto, iva: row._iva, total: row._total },
    referencias: [
      {
        tipo: TIPO_REF_ORDEN_COMPRA,
        folio: row['folio orden compra'],
        fecha: row['fecha orden compra'],
        razon: row['razon orden compra'] || 'ORDEN DE COMPRA',
      },
      {
        tipo: TIPO_REF_HES,
        folio: row['folio hes'],
        fecha: row['fecha hes'],
        razon: row['razon hes'] || 'HES',
      },
    ],
  });

  // Los errores del backend vienen como { index, mensaje }, con index = posición de
  // la fila en el arreglo enviado, que es el mismo orden de la vista previa.
  const marcarErroresDelServidor = (erroresServidor) => {
    const porFila = new Map();
    erroresServidor.forEach((e) => {
      const indice = Number(e?.index);
      if (Number.isInteger(indice)) porFila.set(indice, e?.mensaje || 'Rechazada por el servidor');
    });
    setRows((prev) => prev.map((row, i) => (
      porFila.has(i) ? { ...row, error: porFila.get(i) } : row
    )));
  };

  const handleSend = () => {
    const hasErrores = rows.some((r) => r.error);
    if (hasErrores) {
      setErrorMessageModal('Algunas filas contienen errores. Revisa los datos y corrige los problemas antes de enviar.');
      setShowErrorModal(true);
      return;
    }
    const payload = JSON.stringify(rows.map(buildPayloadRow));
    setLoading(true);
    api.uploadFacturas(payload, esExenta)
      .then((response) => {
        const data = response.data?.body ? JSON.parse(response.data.body) : response.data;
        // El backend devuelve el arreglo de documentos generados; ese mismo arreglo se usa para el ZIP.
        const documentos = Array.isArray(data)
          ? data
          : (data?.documentos || data?.facturas || data?.folios || data?.assignedFolios || []);
        const folios = documentos
          .map((d) => (d && typeof d === 'object' ? (d.folio ?? d.Folio ?? d.FOLIO) : d))
          .filter((f) => f !== undefined && f !== null && f !== '');
        setProcessedDocs(documentos);
        setProcessedFolios(folios);
        if (documentos.length > 0) {
          setShowSuccessModal(true);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setRows([]);
          setErrors([]);
          setFileName('');
        } else {
          setErrorMessageModal('El servidor no devolvió documentos. Verifica el estado de las facturas.');
          setShowErrorModal(true);
        }
      })
      .catch((error) => {
        console.error('Error al enviar las facturas:', error);
        // El backend valida el lote completo y, si algo falla, no emite ninguna
        // factura y detalla qué fila rechazó. Mostramos ese detalle sobre la misma
        // vista previa en vez de un "ocurrió un error" que obliga a adivinar.
        const erroresServidor = erroresDeRespuesta(error);
        if (erroresServidor) {
          marcarErroresDelServidor(erroresServidor);
          setErrorMessageModal(
            `El servidor rechazó ${erroresServidor.length} ${erroresServidor.length === 1 ? 'fila' : 'filas'}. `
            + 'No se emitió ninguna factura del lote: corrige las filas marcadas y vuelve a enviar.',
          );
        } else {
          setErrorMessageModal(
            mensajeDeRespuesta(error) || 'Ocurrió un error al enviar las facturas. Intenta nuevamente.',
          );
        }
        setShowErrorModal(true);
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const res = await api.downloadZip({ documentos: JSON.stringify(processedDocs) });
      const url = res.data;
      if (!url) {
        console.error('No se recibió una URL válida para el ZIP');
        return;
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error al descargar el ZIP:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [...requiredColumns];
    // Sin sucursales cargadas las columnas de emisor van en blanco: prefiero eso a
    // dejar una dirección de ejemplo que alguien termine emitiendo tal cual.
    const emisorPorDefecto = {
      'direccion emisor': sucursalPorDefecto?.direccion || '',
      'comuna emisor': sucursalPorDefecto?.comuna || '',
      'ciudad emisor': sucursalPorDefecto?.ciudad || '',
      'codigo sii emisor': sucursalPorDefecto?.codigo_sii || '',
    };
    // Ejemplos: neto = cantidad * precio; en afecta el total = neto + IVA, en exenta total = neto.
    const examples = [
      {
        ...emisorPorDefecto,
        'rut receptor': '12345678-5', 'razon social receptor': 'Comercial Andes SpA',
        'giro receptor': 'Venta de equipos',
        'direccion receptor': 'Av. Providencia 123', 'comuna receptor': 'Providencia', 'ciudad receptor': 'Santiago',
        'fecha emision': '2026-06-25', 'nombre producto': 'Servicio de mantencion',
        'cantidad': 1, 'unidad': 'UN', 'precio': 100000, 'iva': 19000,
        'valor total': esExenta ? 100000 : 119000,
        'folio orden compra': 'OC-2026-001', 'fecha orden compra': '2026-06-20',
        'razon orden compra': 'Orden de compra mantencion',
        'folio hes': 'HES-5500', 'fecha hes': '2026-06-22', 'razon hes': 'HES mantencion',
        'email receptor': 'contacto@andes.cl', 'descripcion': 'Mantencion preventiva trimestral',
        'forma pago': 'contado', 'tipo venta': 1,
      },
      {
        ...emisorPorDefecto,
        'rut receptor': '11111111-1', 'razon social receptor': 'Constructora Sur Ltda',
        'giro receptor': 'Construccion',
        'direccion receptor': 'Calle Sur 456', 'comuna receptor': 'Maipu', 'ciudad receptor': 'Santiago',
        'fecha emision': '2026-06-25', 'nombre producto': 'Arriendo maquinaria',
        'cantidad': 2, 'unidad': 'UN', 'precio': 250000, 'iva': 95000,
        'valor total': esExenta ? 500000 : 595000,
        'folio orden compra': 'OC-2026-002', 'fecha orden compra': '2026-06-18',
        'razon orden compra': 'Orden de compra arriendo',
        'folio hes': 'HES-5501', 'fecha hes': '2026-06-21', 'razon hes': 'HES arriendo',
        'email receptor': 'pagos@constructorasur.cl', 'descripcion': '',
        'forma pago': 'credito', 'tipo venta': 2,
      },
    ];
    const aoa = [headers, ...examples.map((r) => headers.map((c) => r[c] ?? ''))];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = headers.map((c) => ({ wch: Math.max(12, COLUMN_LABELS[c]?.length ?? c.length) + 4 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, `plantilla_facturas_${tipoLabel.toLowerCase()}.xlsx`);
  };

  const handleLimpiar = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setRows([]);
    setErrors([]);
    setProcessedFolios([]);
    setProcessedDocs([]);
    setFileName('');
  };

  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const conError = rows.filter((r) => r.error).length;
    return { total: rows.length, validas: rows.length - conError, conError };
  }, [rows]);

  const formatMiles = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '';
    return valor.toLocaleString('es-CL');
  };

  return (
    <div className="EmisorMasivo">
      <div className="page-header">
        <h1 className="page-title">Emisor Masivo de Facturas {tipoLabel}s</h1>
        <p className="page-subtitle">
          Sube un Excel con facturas {tipoLabel.toLowerCase()}s y emítelas en lote
          (un producto y dos referencias: Orden de Compra y HES)
        </p>
      </div>

      <div className="search-card">
        <div className="search-card-header">
          <FaCloudUploadAlt className="me-2" />
          Subir archivo Excel
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownloadTemplate}
            className="ms-auto template-btn"
          >
            <FaDownload className="me-2" /><FaFileExcel className="me-1" />Plantilla
          </Button>
        </div>
        <div className="card-body">
          <div
            className={`dropzone ${dragging ? 'dropzone-dragging' : ''} ${fileName ? 'dropzone-with-file' : ''}`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current.click(); }}
          >
            {fileName ? (
              <>
                <FaFileExcel className="dropzone-icon dropzone-icon-success" />
                <div className="dropzone-title">{fileName}</div>
                <div className="dropzone-hint">Haz clic para reemplazar el archivo</div>
              </>
            ) : (
              <>
                <FaCloudUploadAlt className="dropzone-icon" />
                <div className="dropzone-title">Arrastra tu Excel aquí o haz clic para seleccionar</div>
                <div className="dropzone-hint">Solo archivos .xlsx o .xls</div>
              </>
            )}
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </div>

          <div className="info-box">
            <FaInfoCircle className="info-icon" />
            <div>
              <strong>Columnas requeridas:</strong>
              <div className="info-cols">
                {requiredColumns.map((col) => (
                  <code key={col}>{col}</code>
                ))}
              </div>
              <div className="info-sub">
                El nombre de cada columna debe coincidir exactamente.
                Descarga la plantilla para ver un ejemplo.
              </div>

              <dl className="info-rules">
                <dt>Se pueden dejar en blanco</dt>
                <dd>
                  <code>email receptor</code>, <code>descripcion</code>, <code>tipo venta</code> y
                  las cuatro columnas de emisor, incluida <code>codigo sii emisor</code>. El resto es
                  obligatorio en cada fila.
                </dd>

                <dt>Dirección del emisor</dt>
                <dd>
                  Es la dirección desde la que emites, y cada celda que dejes vacía se completa con
                  el dato de la empresa. La plantilla trae la casa matriz ya escrita: cámbiala en las
                  filas que salgan desde otra dirección.
                </dd>

                <dt><code>codigo sii emisor</code></dt>
                <dd>
                  Opcional. Es el código que el SII le asigna al local, hasta 9 dígitos. Si lo dejas
                  en blanco el documento sale sin ese dato, y se emite igual.
                </dd>

                <dt><code>tipo venta</code></dt>
                <dd>
                  Va por código: <code>1</code> ventas del giro, <code>2</code> venta de activo fijo,{' '}
                  <code>3</code> venta de bien raíz. En blanco se emite como <code>1</code>.
                </dd>

                <dt><code>forma pago</code></dt>
                <dd>Obligatoria, y solo acepta <code>contado</code> o <code>credito</code>.</dd>

                <dt>Montos</dt>
                <dd>
                  {esExenta
                    ? 'En exenta no hay IVA: el valor total debe ser igual a cantidad × precio.'
                    : 'El IVA lo ingresas tú y el valor total debe cuadrar: neto + IVA, con neto = cantidad × precio.'}
                </dd>
              </dl>
            </div>
          </div>

          {errors.length > 0 && (
            <div className="alert-box alert-danger-box">
              <FaExclamationTriangle className="alert-box-icon" />
              <div>
                <strong>Errores detectados:</strong>
                <ul className="m-0 mt-1">
                  {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-neutral"><FaTable /></div>
            <div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Filas totales</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success"><FaCheckCircle /></div>
            <div>
              <div className="stat-value">{stats.validas}</div>
              <div className="stat-label">Listas para emitir</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-danger"><FaExclamationCircle /></div>
            <div>
              <div className="stat-value">{stats.conError}</div>
              <div className="stat-label">Con errores</div>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="search-card mt-4">
          <div className="search-card-header">
            <FaTable className="me-2" />
            Vista previa
            <span className="results-count">{rows.length}</span>
          </div>
          <div className="p-0">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    {requiredColumns.map((col) => (
                      <th key={col} className={NUM_COLUMNS.has(col) ? 'num' : ''}>{COLUMN_LABELS[col]}</th>
                    ))}
                    <th className="num">Neto (calc)</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={row.error ? 'row-error' : 'row-ok'}>
                      {requiredColumns.map((col) => (
                        <td key={col} className={NUM_COLUMNS.has(col) ? 'num' : ''}>
                          {col === 'rut receptor' && row['rut_display']
                            ? row['rut_display']
                            : NUM_COLUMNS.has(col) && typeof row[col] === 'number'
                              ? formatMiles(row[col])
                              : row[col]}
                        </td>
                      ))}
                      <td className="num">{formatMiles(row._neto)}</td>
                      <td>
                        {row.error ? (
                          <span className="status-pill status-pill-error" title={row.error}>
                            <FaExclamationCircle className="me-1" />
                            {row.error}
                          </span>
                        ) : (
                          <span className="status-pill status-pill-success">
                            <FaCheckCircle className="me-1" />Listo
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="table-footer-actions">
            <Button variant="outline-secondary" onClick={handleLimpiar}>
              <FaEraser className="me-2" />Limpiar
            </Button>
            <Button
              variant="primary"
              className="ms-auto"
              disabled={rows.some((r) => r.error) || loading}
              onClick={handleSend}
            >
              <FaPaperPlane className="me-2" />Emitir facturas {tipoLabel.toLowerCase()}s
            </Button>
          </div>
        </div>
      )}

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-danger me-2" />
            Error al enviar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p className="m-0">{errorMessageModal}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCheckCircle className="text-success me-2" />
            Facturas emitidas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Se procesaron correctamente los siguientes folios:</p>
          <div className="folios-box">
            {processedFolios.length > 0 ? (
              <ul className="folios-list">
                {processedFolios.map((f, idx) => <li key={idx}>Folio <strong>{f}</strong></li>)}
              </ul>
            ) : (
              <em>No se recibieron folios.</em>
            )}
          </div>
          <p className="mt-3 mb-0 text-muted">
            Puedes descargar un ZIP con los PDFs y un Excel con el detalle.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>Cerrar</Button>
          <Button
            variant="primary"
            onClick={handleDownloadZip}
            disabled={downloadingZip || processedDocs.length === 0}
          >
            {downloadingZip ? (
              <><Spinner animation="border" size="sm" className="me-2" />Preparando…</>
            ) : (
              <><FaFileArchive className="me-2" />Descargar ZIP</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="m-0 modal-message">Cargando facturas, por favor espere…</p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmisorMasivoFactura;
